// Firebase Configuration for Mercapoli Move
// Push Notifications via Firebase Cloud Messaging
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

// Firebase configuration from environment
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Check if Firebase is configured
const isFirebaseConfigured = () => {
  return firebaseConfig.apiKey && 
         firebaseConfig.projectId && 
         firebaseConfig.messagingSenderId;
};

// Initialize Firebase app
let app = null;
let messaging = null;

export const initializeFirebase = async () => {
  if (!isFirebaseConfigured()) {
    console.warn('Firebase not configured. Push notifications disabled.');
    return null;
  }
  
  try {
    // Check if messaging is supported in this browser
    const supported = await isSupported();
    if (!supported) {
      console.warn('Firebase Messaging not supported in this browser');
      return null;
    }
    
    // Initialize app if not already done
    if (!getApps().length) {
      app = initializeApp(firebaseConfig);
    } else {
      app = getApps()[0];
    }
    
    messaging = getMessaging(app);
    console.log('Firebase initialized successfully');
    return messaging;
  } catch (error) {
    console.error('Failed to initialize Firebase:', error);
    return null;
  }
};

// Request notification permission and get FCM token
export const requestNotificationPermission = async () => {
  try {
    // Check browser support
    if (!('Notification' in window)) {
      console.warn('Notifications not supported');
      return { granted: false, reason: 'not_supported' };
    }
    
    // Request permission
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return { granted: false, reason: 'denied' };
    }
    
    // Initialize Firebase if needed
    if (!messaging) {
      await initializeFirebase();
    }
    
    if (!messaging) {
      return { granted: true, reason: 'firebase_not_configured', token: null };
    }
    
    // Get FCM token
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    const token = await getToken(messaging, { vapidKey });
    
    if (token) {
      console.log('FCM Token obtained');
      return { granted: true, token };
    } else {
      console.warn('Failed to get FCM token');
      return { granted: true, token: null };
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return { granted: false, reason: 'error', error: error.message };
  }
};

// Register service worker manually
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service Worker not supported');
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    console.log('Service Worker registered:', registration.scope);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
};

// Listen for foreground messages
export const onForegroundMessage = (callback) => {
  if (!messaging) {
    console.warn('Messaging not initialized');
    return () => {};
  }
  
  return onMessage(messaging, (payload) => {
    console.log('Foreground message received:', payload);
    callback(payload);
  });
};

// Get current permission status
export const getNotificationPermission = () => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
};

// Check if Firebase is enabled
export const isFirebaseEnabled = () => {
  return isFirebaseConfigured();
};

export default {
  initializeFirebase,
  requestNotificationPermission,
  registerServiceWorker,
  onForegroundMessage,
  getNotificationPermission,
  isFirebaseEnabled
};
